// import { getSpyderAnalytics } from '@/db/SpyderDB'
import FirebaseAPI from '@/api/firebase'
import ForeplayAPI from '@/api/foreplayServer'
import { brandMatchesFolderEntry } from '@/utils/spyder/brandFolderUtils'

export default {
  namespaced: true,
  state: {
    // Brands the user is subscribed to, in the new Spyder API's shape
    spyderSubscriptions: [],
    // Brands that have been added but are still being processed by the backend
    pendingSpyderBrands: [],
    spyderSubscriptionsLoading: true,
    brandAnalytics: [],
    brandAnalyticsLoading: false,
    // The ?search= / ?page= params the brands list was last left on, so SpyderView's back button can reopen them
    brandsListQuery: {}
  },
  getters: {
    // Get Spyder Subscriptions
    getSpyderSubscriptions: state => state.spyderSubscriptions,
    getPendingSpyderBrands: state => state.pendingSpyderBrands,
    getSpyderSubscriptionsLoading: state => state.spyderSubscriptionsLoading,
    // Brands still being built use a credit too
    getSpyderCreditsUsed: state => state.spyderSubscriptions.length + state.pendingSpyderBrands.length,
    // Tracking more brands than the plan allows (e.g. after a downgrade). A limit of 0 is left to the
    // Spyder paywall, which the old overage endpoint also skipped by rejecting unpaid users.
    getIsOverSpyderLimit: (state, getters, rootState, rootGetters) => {
      const limit = rootGetters['AuthModule/getSpyderLimit'] || 0
      return !state.spyderSubscriptionsLoading && limit > 0 && getters.getSpyderCreditsUsed > limit
    },
    getBrandsListQuery: state => state.brandsListQuery
  },
  mutations: {
    // Set Spyder Subscriptions
    SET_SPYDER_SUBSCRIPTIONS (state, subscriptions) {
      state.spyderSubscriptions = subscriptions
    },
    SET_PENDING_SPYDER_BRANDS (state, brands) {
      state.pendingSpyderBrands = brands
    },
    SET_SPYDER_SUBSCRIPTIONS_LOADING (state, loading) {
      state.spyderSubscriptionsLoading = loading
    },
    SET_BRAND_ANALYTICS (state, analytics) {
      state.brandAnalytics = analytics
    },
    SET_BRANDS_LIST_QUERY (state, query) {
      state.brandsListQuery = query
    }
  },
  actions: {
    async fetchSpyderLimit ({ rootState }) {
      const limitOwner = rootState.AuthModule?.team?.created_by || rootState.AuthModule.user?.user_id

      const owner = await FirebaseAPI.Users.getById(limitOwner)

      const hasLimit = owner.spyderLimit !== undefined && owner.spyderLimit !== null
      return hasLimit ? owner.spyderLimit : 2
    },
    // Fetch Spyder Subscriptions
    async fetchSpyderSubscriptions ({ commit }) {
      try {
        const { subscribed, pending } = await ForeplayAPI.Spyder.getBrandSubscriptions()
        commit('SET_SPYDER_SUBSCRIPTIONS', subscribed || [])
        commit('SET_PENDING_SPYDER_BRANDS', pending || [])
      } finally {
        commit('SET_SPYDER_SUBSCRIPTIONS_LOADING', false)
      }
    },
    // Removes brands from Spyder: unsubscribes from built ones and cancels pending ones. Takes normalized brands
    // (see normalizeBrandsForSubscriptionsList) or raw subscribed brands. The server also takes them out of their
    // folders, which is mirrored in the store.
    async removeSpyderBrands ({ commit, state, rootGetters }, brands = []) {
      const builtBrands = brands.filter(brand => !brand.pending)
      const pendingBrands = brands.filter(brand => brand.pending)
      const pendingRefOf = brand => `${brand.brand.ref_type}:${brand.brand.ref}`
      const pendingOfType = refType => pendingBrands.filter(brand => brand.brand.ref_type === refType).map(brand => brand.brand.ref)

      const [, cancelResult] = await Promise.all([
        builtBrands.length ? ForeplayAPI.Spyder.unsubscribeFromBrands(builtBrands.map(brand => brand.id)) : null,
        pendingBrands.length
          ? ForeplayAPI.Spyder.cancelPendingBrands({ advertiserIds: pendingOfType('meta_ad_library'), domains: pendingOfType('domain') })
          : null
      ])

      const canceledRefs = new Set((cancelResult?.data?.canceled || []).map(({ ref_type: refType, ref }) => `${refType}:${ref}`))
      const builtIds = new Set(builtBrands.map(brand => brand.id))
      commit('SET_SPYDER_SUBSCRIPTIONS', state.spyderSubscriptions.filter(brand => !builtIds.has(brand.id)))
      commit('SET_PENDING_SPYDER_BRANDS', state.pendingSpyderBrands.filter(brand => !canceledRefs.has(`${brand.ref_type}:${brand.ref}`)))

      const removedBrands = [...builtBrands, ...pendingBrands.filter(brand => canceledRefs.has(pendingRefOf(brand)))]
      const folders = rootGetters['BoardsModule/getFolders']
      commit('BoardsModule/SET_FOLDERS', folders.map(folder => {
        const entries = folder.spyderBrands || []
        const keptEntries = entries.filter(entry => !removedBrands.some(brand => brandMatchesFolderEntry(brand, entry)))
        return keptEntries.length === entries.length ? folder : { ...folder, spyderBrands: keptEntries }
      }), { root: true })

      return { notCanceled: pendingBrands.filter(brand => !canceledRefs.has(pendingRefOf(brand))) }
    }
  }
}
